import moment from 'moment';

const START_OF_WORKDAY = 9;
const END_OF_WORKDAY = 17;
const DEFAULT_EXCLUDE_OPTIONS = {
  type: 0,
  workStart: START_OF_WORKDAY,
  workEnd: END_OF_WORKDAY
};

function chunkRange(start, end) {
  const roundStart = start.endOfDay();
  const roundEnd = end.startOfDay();
  const diff = roundEnd < roundStart ? 0 : roundEnd.diff(roundStart);
  if (!diff) return [{ start, end }];

  const arr = [];
  if (+start !== +roundStart) arr.push({ start, end: roundStart });
  if (diff > 1) {
    for (let i = roundStart; i.isBefore(roundEnd); i = i.addDays(1)) {
      const d = i.addDays(1);
      arr.push({ start: d.startOfDay(), end: d });
    }
    arr.pop();
  }
  if (+roundEnd !== +end) arr.push({ start: roundEnd, end });
  return arr;
}

function getWorkHours(day, workStart, workEnd) {
  const startOfDay = new Date(day.start);
  const startTime = new moment(workStart, 'HH:mm');
  startOfDay.setHours(startTime.get('hour'), startTime.get('minute'), 0, 0);

  const endOfDay = new Date(day.end);
  const endTime = new moment(workEnd, 'HH:mm');
  endOfDay.setHours(endTime.get('hour'), endTime.get('minute'), 0, 0);

  if (day.start.isSameOrAfter(endOfDay)) {
    return 0;
  }

  if (day.start.isSameOrAfter(startOfDay) && day.end.isSameOrBefore(endOfDay)) {
    return day.end.getTime() - day.start.getTime();
  } else if (day.start.isBefore(startOfDay) && day.end.isSameOrBefore(endOfDay)) {
    return day.end.getTime() - startOfDay.getTime();
  } else if (day.start.isSameOrAfter(startOfDay) && day.end.isAfter(endOfDay)) {
    return endOfDay.getTime() - day.start.getTime();
  } else {
    return endOfDay.getTime() - startOfDay.getTime();
  }
}

function checkForHoliday(date, holidays = []) {
  for (let i = 0; i < holidays.length; i++) {
    if (date.isSameDay(holidays[i])) {
      return true;
    }
  }

  return false;
}

Date.prototype.substract = function(days) {
  const date = new Date(this.valueOf());

  date.setDate(date.getDate() - days);
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.addDays = function(days) {
  const date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

Date.prototype.startOfDay = function() {
  const date = new Date(this.valueOf());
  date.setHours(0, 0, 0, 0);
  return date;
};

Date.prototype.endOfDay = function() {
  const date = new Date(this.valueOf());
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.endOfWeek = function(weekStartsOn = 1, numOfWeeks = 1) {
  const date = new Date(this.valueOf());
  const day = date.getDay();
  const diff = (day < weekStartsOn ? -7 : 0) + 6 - (day - weekStartsOn) + (numOfWeeks > 1 ? (numOfWeeks - 1) * 7 : 0);

  date.setDate(date.getDate() + diff);
  date.setHours(23, 59, 59, 999);

  return date;
};

Date.prototype.endOfMonth = function() {
  const date = new Date(this.valueOf());
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

Date.prototype.startOfMonth = function() {
  const date = new Date(this.valueOf());
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month, 1);
  date.setHours(23, 59, 59, 999);

  return date;
};

Date.prototype.endOf = function(unit, numOfWeeks = 1) {
  switch (unit) {
    case 'week': {
      return new Date(this.valueOf()).endOfWeek(1, numOfWeeks);
    }
    case 'month': {
      return new Date(this.valueOf()).endOfMonth();
    }
    default: {
      return new Date(this.valueOf()).endOfDay();
    }
  }
};

Date.prototype.inBetween = function(start, from) {
  const day = new Date(this.valueOf()).getTime();
  const startDay = new Date(start).getTime();
  const fromDay = new Date(from).getTime();

  return day >= startDay && day <= fromDay;
};

Date.prototype.isSameOrAfter = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day >= otherDay;
};

Date.prototype.isAfter = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day > otherDay;
};

Date.prototype.isSameOrBefore = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day <= otherDay;
};

Date.prototype.isBefore = function(other) {
  const day = new Date(this.valueOf()).getTime();
  const otherDay = new Date(other).getTime();

  return day < otherDay;
};

Date.prototype.isSameDay = function(other) {
  const day = new Date(this.valueOf());
  const otherDay = new Date(other);

  return (
    day.getDate() === otherDay.getDate() &&
    day.getMonth() === otherDay.getMonth() &&
    day.getFullYear() === otherDay.getFullYear()
  );
};

Date.prototype.isWeekend = function() {
  return (new Date(this.valueOf()).getDay() || 7) > 5;
};

Date.prototype.diff = function(other, unit, excludeOpts = DEFAULT_EXCLUDE_OPTIONS, absolute = true) {
  let day = new Date(this.valueOf());
  let otherDay = new Date(other);

  let timeDiff;
  if (absolute) {
    timeDiff = Math.abs(otherDay.getTime() - day.getTime());
  } else {
    timeDiff = otherDay.getTime() - day.getTime();
    if (timeDiff < 0) {
      timeDiff = 60000;
    }
  }

  let workTimeDiff = timeDiff;
  if (+excludeOpts.type > 0) {
    //Check if second date is before first date to switch
    if (day.isBefore(otherDay)) {
      day = new Date(other);
      otherDay = new Date(this.valueOf());
    }

    if (+excludeOpts.type === 1) {
      const d = otherDay;
      while (d <= day) {
        if (d.isWeekend() || checkForHoliday(d, excludeOpts.holidays)) {
          workTimeDiff -= 24 * 60 * 60 * 1000;
        }
        d.setDate(d.getDate() + 1);
      }
    } else {
      const days = chunkRange(otherDay, day);
      workTimeDiff = 0;

      for (let i = 0; i < days.length; i++) {
        const d = days[i];
        if (d.start.isWeekend() || checkForHoliday(d.start, excludeOpts.holidays)) {
          continue;
        }

        if (days.length > 1) {
          if (i === 0 || i === days.length - 1) {
            workTimeDiff += getWorkHours(d, excludeOpts.workStart, excludeOpts.workEnd);
          } else {
            const workMinutes = moment(excludeOpts.workEnd, 'HH:mm').diff(
              moment(excludeOpts.workStart, 'HH:mm'),
              'milliseconds'
            );
            workTimeDiff += workMinutes;
          }
        } else {
          workTimeDiff = getWorkHours(d, excludeOpts.workStart, excludeOpts.workEnd);
        }
      }
    }

    if (workTimeDiff < 0) {
      workTimeDiff = 0;
    }
  }

  switch (unit) {
    case 'minutes': {
      return Math.ceil(workTimeDiff / (1000 * 60));
    }
    case 'hours': {
      return Math.ceil(workTimeDiff / (1000 * 3600));
    }
    case 'days': {
      return Math.ceil(workTimeDiff / (1000 * 3600 * 24));
    }
    default: {
      return workTimeDiff;
    }
  }
};

function createHighchartFormats(format) {
  const obj = {};

  switch (format) {
    case 'DD MMM YYYY': {
      obj.day = momentToStrftimeFormat('DD MMM');
      obj.week = momentToStrftimeFormat('DD MMM');
      obj.month = momentToStrftimeFormat('MMM YYYY');
      break;
    }
    case 'DD-MM-YYYY': {
      obj.day = momentToStrftimeFormat('DD-MM');
      obj.week = momentToStrftimeFormat('DD-MM');
      obj.month = momentToStrftimeFormat('MM-YYYY');
      break;
    }
    case 'DD/MM/YYYY': {
      obj.day = momentToStrftimeFormat('DD/MM');
      obj.week = momentToStrftimeFormat('DD/MM');
      obj.month = momentToStrftimeFormat('MM/YYYY');
      break;
    }
    case 'MMM DD, YYYY': {
      obj.day = momentToStrftimeFormat('MMM DD');
      obj.week = momentToStrftimeFormat('MMM DD');
      obj.month = momentToStrftimeFormat('MMM YYYY');
      break;
    }
    case 'MM-DD-YYYY': {
      obj.day = momentToStrftimeFormat('MM-DD');
      obj.week = momentToStrftimeFormat('MM-DD');
      obj.month = momentToStrftimeFormat('MM-YYYY');
      break;
    }
    case 'MM/DD/YYYY': {
      obj.day = momentToStrftimeFormat('MM/DD');
      obj.week = momentToStrftimeFormat('MM/DD');
      obj.month = momentToStrftimeFormat('MM/YYYY');
      break;
    }
    case 'YYYY-MM-DD': {
      obj.day = momentToStrftimeFormat('MM-DD');
      obj.week = momentToStrftimeFormat('MM-DD');
      obj.month = momentToStrftimeFormat('YYYY-MM');
      break;
    }
    case 'YYYY/MM/DD': {
      obj.day = momentToStrftimeFormat('MM/DD');
      obj.week = momentToStrftimeFormat('MM/DD');
      obj.month = momentToStrftimeFormat('YYYY/MM');
      break;
    }
  }

  return {
    ...obj,
    millisecond: obj.day,
    second: obj.day,
    minute: obj.day,
    hour: obj.day
  };
}

function getFirstOfMonth(selectedDate) {
  const date = new Date(selectedDate);
  const month = date.getMonth();
  date.setFullYear(date.getFullYear(), month, 1);
  date.setHours(23, 59, 59, 999);

  return date;
}

function momentToStrftimeFormat(format) {
  const conversions = {
    DD: '%d',
    YYYY: '%Y',
    MM: '%m',
    MMM: '%b'
  };

  return format.replace(/[A-Za-z]+/g, match => {
    return conversions[match] || match;
  });
}

function getDays(from, to) {
  if (moment(to).isAfter()) {
    to = moment.now();
  }

  return moment(to).diff(moment(from), 'days') + 1;
}

function generateDateRangeArray(from, to) {
  const days = getDays(from, to);
  const arr = [];
  for (let i = 0; i < days; i++) {
    arr.push({
      date: new Date(from).addDays(i)
    });
  }

  return arr;
}

function generateWeeksRangeArray(from, to) {
  from = new Date(from).setHours(0, 0, 0);
  to = new Date(to).setHours(23, 59, 59);

  const days = getDays(from, to);
  const arr = [];
  for (let i = 0; i < days; ) {
    arr.push({
      from: new Date(from).addDays(i),
      to: new Date(from).addDays(i + 13).setHours(23, 59, 59)
    });

    i += 14;
  }

  return arr;
}

export default {
  getDays,
  createHighchartFormats,
  momentToStrftimeFormat,
  generateDateRangeArray,
  generateWeeksRangeArray
};
